import { ClientError, GraphQLClient } from 'graphql-request';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';

import { ACCESS_TOKEN_KEY } from '@/constants';
import { getHTTPHeaders, GRAPHQL_URL } from '@shared/auth/Authentication.hooks';
import { clearTokens } from '@shared/auth/tokens';
import { refreshTokenSingleFlight } from '@shared/auth/refresh';

/**
 * The single authenticated GraphQL entry point for the app.
 *
 * Replaces the old `QueryRequest` class (`shared/query-client/QueryClient.ts`),
 * which every view hook reached for via `AppContext`. Two things changed in the
 * move, both deliberate:
 *
 * 1. **No mutable client, no `rebuild` callback.** `QueryRequest` held a long-lived
 *    `GraphQLClient` whose `Authorization` header had to be patched after a token
 *    refresh, plus a `rebuild` hook that `App.tsx` reassigned *during render* to
 *    rebuild the React-held client. Here headers are read fresh from storage on
 *    every call, so a refreshed token is picked up by the next request with no
 *    coordination at all and that whole mechanism disappears.
 * 2. **One implementation instead of two.** `query()` and `queryWithClient()` had
 *    near-identical 401 handling that had already drifted apart slightly; callers
 *    that needed anonymous or admin headers hand-built their own `GraphQLClient`
 *    and passed it in. That is now the `withAuth` flag.
 *
 * The 401-refresh-and-retry behaviour was ported as-is from `QueryRequest`,
 * including a known defect (see `onUnauthenticated` below), so the topic-3
 * extraction stayed behaviour-neutral. Topic 4 (auth) owns refresh
 * correctness and has since fixed the refresh call itself to be
 * single-flight (`services/auth/refresh.ts`) — the whole-cache invalidation
 * on 401 and the hard `clearTokens('/')` redirect on refresh failure are
 * still open, see `improvements/04-authentication-security.md`.
 *
 * Two call shapes (topic 5, 2026-08-13): pass a codegen'd `TypedDocumentNode`
 * (from `services/api/gql`'s `graphql()` tag) and both `T`/`V` are inferred
 * automatically — no explicit generic, no hand-maintained response type.
 * The plain-`string` overload still exists for the many call sites not yet
 * migrated off hand-rolled query strings; new code should prefer the typed
 * one.
 */
export async function graphqlFetch<T, V extends Record<string, unknown> | undefined>(
	document: TypedDocumentNode<T, V>,
	variables?: V,
	options?: GraphqlFetchOptions,
): Promise<T>;
export async function graphqlFetch<
	T,
	// Defaulted to the permissive shape, not `undefined`: callers routinely pin
	// only the result type (`graphqlFetch<PreviewQuery>(doc, vars)`), and with
	// `undefined` as the default TypeScript would then reject every `vars`
	// argument rather than inferring it.
	V extends Record<string, unknown> | undefined = Record<string, unknown> | undefined,
>(document: string, variables?: V, options?: GraphqlFetchOptions): Promise<T>;
export async function graphqlFetch<T, V extends Record<string, unknown> | undefined>(
	document: string | TypedDocumentNode<T, V>,
	variables?: V,
	options: GraphqlFetchOptions = {},
): Promise<T> {
	const { withAuth = true } = options;

	const run = () =>
		new GraphQLClient(GRAPHQL_URL, {
			headers: getHTTPHeaders(withAuth),
		}).request<T>(document as string, variables as Record<string, unknown> | undefined);

	try {
		return await run();
	} catch (error) {
		if (!(error instanceof ClientError)) throw error;
		if (!isUnauthenticated(error)) throw error;

		// PORTED AS-IS / KNOWN DEFECT: this invalidates *every* query in the
		// cache on any single 401, not just the one that failed, and it does so
		// before the refresh has even been attempted. Preserved deliberately to
		// keep this extraction behaviour-neutral; topic 4 owns the fix.
		onUnauthenticated?.();

		try {
			const refreshed = await refreshTokenSingleFlight();
			if (!refreshed) {
				throw new Error('Session expired. Please log in again.');
			}
			if (!localStorage.getItem(ACCESS_TOKEN_KEY)) {
				throw new Error('Session expired. Please log in again.');
			}
			// No header patching needed: `run()` re-reads headers from storage.
			return await run();
		} catch {
			clearTokens('/');
			return Promise.reject(
				new Error('Session expired. Please log in again.'),
			);
		}
	}
}

export interface GraphqlFetchOptions {
	/**
	 * Send the `Authorization` header. `false` reproduces the call sites that
	 * used to hand-build an anonymous `GraphQLClient` via `getHTTPHeaders(false)`
	 * and pass it to `queryWithClient`. Defaults to `true`.
	 */
	withAuth?: boolean;
}

/**
 * Set once at app start so this module can invalidate the query cache on a 401
 * without importing the QueryClient (which would re-create the services →
 * query-client → views/templates import cycle this extraction is untangling).
 */
let onUnauthenticated: (() => void) | undefined;

export function setUnauthenticatedHandler(handler: () => void): void {
	onUnauthenticated = handler;
}

function isUnauthenticated(error: ClientError): boolean {
	return !!error.response.errors?.some(
		(err) =>
			err.message.includes('jwt expired') ||
			err.extensions?.code === 'UNAUTHENTICATED',
	);
}
